import React, { useEffect, useState } from 'react';
import Spinner from "../../../../components/Spinner/Spinner";
import reportHubApi from "../../../../services/Customer/reportHubApi";
import HHSReportView from "./HHSReportView";

export type HHRDataType = {
  charts: {
    type: string;
    name: string;
    params: {
      value?: number; // For gauge_chart
      min_value?: number; // For gauge_chart
      max_value?: number; // For gauge_chart
      x?: string[]; // For history_chart and history_with_zones_chart
      y?: number[]; // For history_chart and history_with_zones_chart
      min_x?: string; // For history_chart and history_with_zones_chart
      max_x?: string; // For history_chart and history_with_zones_chart
      min_y?: number; // For history_chart and history_with_zones_chart
      max_y?: number; // For history_chart and history_with_zones_chart
      y_label?: string; // For history_with_zones_chart
      zones?:{
        range: [number, number];
        weight: number;
        color: string;
      }[]; // For history_with_zones_chart
      y_ticks?: any[];
      habits?: {
        habit_name: string;
        normalized_value: number;
      }[]; // For contributing_habits_chart
    };
  }[];
  texts: {
    progress_statement_text: { value: string };
    lowest_habit_negative_bool: { value: boolean };
    top_habit_w_InnerAge_string: { value: string };
    top_habit_w_InnerAge_biomarkers: { value: string[] };
    lowest_habit_w_InnerAge_string: { value: string };
    lowest_habit_w_InnerAge_biomarkers: { value: string[] };
    lowest_habit_string: { value: string };
    lowest_habit_alt_text_formatted_name: { value: string };
    lowest_habit_quick_tips_list: { value: string[] };
    has_journal_questions_bool: { value: boolean };
    recent_week_date_str: { value: string };
    InnerAge_section_title: { value: string };
  };
  report_data?: any;
  score_percentile?: number,
  habit_score_history_dict?: any,
  lowest_habit?: string,
  absolute_score_level?: string,
  HHS_status?: any,
  mostRecentWeek?: any,
};

const data: HHRDataType = {
  charts: [
    {
      type: "gauge_chart",
      name: "score_percentile_chart",
      params: {
        value: 64,
        min_value: 0,
        max_value: 100,
      },
    },
    {
      type: "history_chart",
      name: "habit_score_history_chart",
      params: {
        x: [
          "2025-04-07",
          "2025-04-14",
          "2025-04-21",
          "2025-04-28",
          "2025-05-05",
          "2025-05-12",
        ],
        y: [
          57,
          57,
          36,
          57,
          70,
          64,
        ],
        min_x: "2025-04-07",
        max_x: "2025-05-12",
        min_y: 0,
        max_y: 100,
      },
    },
    {
      type: "contributing_habits_chart",
      name: "contributing_habits_chart",
      params: {
        habits: [
          {
            habit_name: "Exercise Hours",
            normalized_value: 1.0,
          },
          {
            habit_name: "Sleep Duration",
            normalized_value: 1.0,
          },
          {
            habit_name: "Bed Time Consistency",
            normalized_value: 0.75,
          },
          {
            habit_name: "Sleep Efficiency",
            normalized_value: 0.38,
          },
          {
            habit_name: "VO2 Max",
            normalized_value: 0.26,
          },
          {
            habit_name: "REM Sleep Percentage",
            normalized_value: 0.01,
          },
          {
            habit_name: "Resting Heart Rate",
            normalized_value: 0.01,
          },
          {
            habit_name: "Deep Sleep Percentage",
            normalized_value: -0.23,
          },
          {
            habit_name: "Step Count",
            normalized_value: -0.98,
          },
        ],
      },
    },
    {
      type: "history_with_zones_chart",
      name: "Steps_chart",
      params: {
        zones: [
          {
            range: [
              0,
              2000,
            ],
            weight: -4,
            color: "",
          },
          {
            range: [
              2000,
              4000,
            ],
            weight: -2,
            color: "",
          },
          {
            range: [
              4000,
              6000,
            ],
            weight: -1,
            color: "",
          },
          {
            range: [
              6000,
              8000,
            ],
            weight: 0,
            color: "#7ee07e",
          },
          {
            range: [
              8000,
              10000,
            ],
            weight: 1,
            color: "#54c054",
          },
          {
            range: [
              10000,
              12000,
            ],
            weight: 2,
            color: "#29a029",
          },
          {
            range: [
              12000,
              100000,
            ],
            weight: 3,
            color: "#008000",
          },
        ],
        x: [
          "2025-02-17",
          "2025-02-24",
          "2025-03-03",
          "2025-03-10",
          "2025-03-17",
          "2025-03-24",
        ],
        y: [
          3830.71,
          4754.0,
          4689.57,
          5281.29,
          7282.6,
          4416.43,
        ],
        min_x: "2025-02-17",
        max_x: "2025-03-24",
        min_y: 2795.14,
        max_y: 10035.57,
        y_ticks: [
          "2000.0",
          "3000.0",
          "4000.0",
          "5000.0",
          "6000.0",
          "7000.0",
          "8000.0",
          "9000.0",
          "10000.0",
          "11000.0",
        ],
        y_label: "Step Count",
      },
    },
  ],
  texts: {
    progress_statement_text: {
      value: "Good job on your habits this week!",
    },
    lowest_habit_negative_bool: {
      value: true,
    },
    top_habit_w_InnerAge_string: {
      value: "Exercise Hours",
    },
    top_habit_w_InnerAge_biomarkers: {
      value: [
        "HDL",
      ],
    },
    lowest_habit_w_InnerAge_string: {
      value: "Step Count",
    },
    lowest_habit_w_InnerAge_biomarkers: {
      value: [
        "LDL",
        "Triglycerides",
        "HbA1c",
        "hsCRP",
        "White Blood Cells",
      ],
    },
    lowest_habit_string: {
      value: "Steps",
    },
    lowest_habit_alt_text_formatted_name: {
      value: "Step Count",
    },
    lowest_habit_quick_tips_list: {
      value: [
        "Take 1,000 steps beyond your average each day\r",
        "Add a 30 minute walk into your daily routine, ideally after a meal",
      ],
    },
    has_journal_questions_bool: {
      value: false,
    },
    recent_week_date_str: {
      value: "2025-03-17",
    },
    InnerAge_section_title: {
      value: "How your top habit may affect your InnerAge",
    },
  },
  report_data: {
    customerId: 385,
    "habit-scores": {
      "2025-03-24": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": 0.0,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": 0.0,
          "Sleep_Efficiency-EWMA": 0.3,
          "Sleep_Duration-TRND": 0.0,
          "Sleep_Duration-EWMA": 0.8,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": 0.0,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.0,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 1.7,
        stimuliSubscore: 1.4,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Same",
          Sleep_Efficiency: "Same",
          Sleep_Duration: "Same",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Same",
          REM_Percentage: "Same",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: 0.0,
          Sleep_Efficiency: 0.3,
          Sleep_Duration: 0.8,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: -0.8,
          REM_Percentage: 0.0,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.3,
      },
      "2025-03-17": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": 0.2,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": -0.2,
          "Sleep_Efficiency-EWMA": 0.1,
          "Sleep_Duration-TRND": -0.8,
          "Sleep_Duration-EWMA": 0.8,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": 0.8,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.2,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 1.7,
        stimuliSubscore: 1.4,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Decreasing",
          Sleep_Efficiency: "Decreasing",
          Sleep_Duration: "Increasing",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Increasing",
          REM_Percentage: "Decreasing",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: 0.2,
          Sleep_Efficiency: -0.1,
          Sleep_Duration: 0.0,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: 0.0,
          REM_Percentage: 0.2,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.30000000000000004,
      },
      "2025-03-10": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": -0.2,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": 0.0,
          "Sleep_Efficiency-EWMA": 0.3,
          "Sleep_Duration-TRND": 0.8,
          "Sleep_Duration-EWMA": -0.2,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": 0.0,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.0,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 1.3,
        stimuliSubscore: 1.2000000000000002,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Increasing",
          Sleep_Efficiency: "Same",
          Sleep_Duration: "Decreasing",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Same",
          REM_Percentage: "Same",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: -0.2,
          Sleep_Efficiency: 0.3,
          Sleep_Duration: 0.6,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: -0.8,
          REM_Percentage: 0.0,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.09999999999999998,
      },
      "2025-03-03": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": 0.0,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": 0.0,
          "Sleep_Efficiency-EWMA": 0.3,
          "Sleep_Duration-TRND": 0.0,
          "Sleep_Duration-EWMA": 0.8,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": 0.0,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.0,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 1.7,
        stimuliSubscore: 1.4,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Same",
          Sleep_Efficiency: "Same",
          Sleep_Duration: "Same",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Same",
          REM_Percentage: "Same",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: 0.0,
          Sleep_Efficiency: 0.3,
          Sleep_Duration: 0.8,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: -0.8,
          REM_Percentage: 0.0,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.3,
      },
      "2025-02-24": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": 0.0,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": 0.0,
          "Sleep_Efficiency-EWMA": 0.3,
          "Sleep_Duration-TRND": 0.0,
          "Sleep_Duration-EWMA": 0.8,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": 0.0,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.0,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 1.7,
        stimuliSubscore: 1.4,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Same",
          Sleep_Efficiency: "Same",
          Sleep_Duration: "Same",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Same",
          REM_Percentage: "Same",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: 0.0,
          Sleep_Efficiency: 0.3,
          Sleep_Duration: 0.8,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: -0.8,
          REM_Percentage: 0.0,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.3,
      },
      "2025-02-17": {
        contributions: {
          "Exercise_Hours-TRND": 0.0,
          "Exercise_Hours-EWMA": 0.8,
          "RHR-TRND": 0.0,
          "RHR-EWMA": 0.0,
          "Sleep_Efficiency-TRND": 0.0,
          "Sleep_Efficiency-EWMA": 0.3,
          "Sleep_Duration-TRND": 0.0,
          "Sleep_Duration-EWMA": 0.8,
          "Deep_Percentage-TRND": 0.0,
          "Deep_Percentage-EWMA": -0.2,
          "Bed_Time_STD-TRND": 0.0,
          "Bed_Time_STD-EWMA": 0.6,
          "Steps-TRND": -0.8,
          "Steps-EWMA": -0.8,
          "REM_Percentage-TRND": 0.0,
          "REM_Percentage-EWMA": 0.0,
          "Vo2_Max-TRND": 0.0,
          "Vo2_Max-EWMA": 0.2,
        },
        score: 0.9,
        stimuliSubscore: 0.6000000000000001,
        trends: {
          Exercise_Hours: "Same",
          RHR: "Same",
          Sleep_Efficiency: "Same",
          Sleep_Duration: "Same",
          Deep_Percentage: "Same",
          Bed_Time_STD: "Same",
          Steps: "Decreasing",
          REM_Percentage: "Same",
          Vo2_Max: "Same",
        },
        combinedContributions: {
          Exercise_Hours: 0.8,
          RHR: 0.0,
          Sleep_Efficiency: 0.3,
          Sleep_Duration: 0.8,
          Deep_Percentage: -0.2,
          Bed_Time_STD: 0.6,
          Steps: -1.6,
          REM_Percentage: 0.0,
          Vo2_Max: 0.2,
        },
        responseSubscore: 0.3,
      },
    },
    habitScoreInformation: {},
    habitAverages: {
      "2025-02-17": {
        RHR: 67.28571428571429,
        Vo2_Max: 37.0,
        Steps: 3830.714285714286,
        Exercise_Hours: 0.6937037037037038,
        Sleep_Efficiency: 0.8925667576561488,
        Sleep_Duration: 7.011904761904761,
        Deep_Percentage: 0.0920189091054506,
        REM_Percentage: 0.1877162321384922,
        Bed_Time_STD: 60.574474436102875,
      },
      "2025-02-24": {
        RHR: 67.5,
        Vo2_Max: 37.0,
        Steps: 4754.0,
        Exercise_Hours: 0.49777777777777776,
        Sleep_Efficiency: 0.9102748455798154,
        Sleep_Duration: 6.973611111111111,
        Deep_Percentage: 0.07022075600010656,
        REM_Percentage: 0.19550856592722002,
        Bed_Time_STD: 41.818013714319235,
      },
      "2025-03-03": {
        RHR: 67.57142857142857,
        Vo2_Max: 37.0,
        Steps: 4689.571428571428,
        Exercise_Hours: 0.7528333333333334,
        Sleep_Efficiency: 0.8991384578572086,
        Sleep_Duration: 7.103571428571429,
        Deep_Percentage: 0.06786869118024805,
        REM_Percentage: 0.11202424737371557,
        Bed_Time_STD: 56.20769494037946,
      },
      "2025-03-10": {
        RHR: 68.14285714285714,
        Vo2_Max: 37.0,
        Steps: 5281.285714285715,
        Exercise_Hours: 0.7205555555555556,
        Sleep_Efficiency: 0.9126899255690015,
        Sleep_Duration: 6.464285714285714,
        Deep_Percentage: 0.07236190661036591,
        REM_Percentage: 0.18376333074751489,
        Bed_Time_STD: 32.84883109917234,
      },
      "2025-03-17": {
        RHR: 67.0,
        Vo2_Max: 37.0,
        Steps: 7282.6,
        Exercise_Hours: 0.6434027777777778,
        Sleep_Efficiency: 0.8800496108150879,
        Sleep_Duration: 8.153333333333332,
        Deep_Percentage: 0.06578289961173801,
        REM_Percentage: 0.0997336489479195,
        Bed_Time_STD: 34.55997290367323,
      },
      "2025-03-24": {
        RHR: 67.0,
        Vo2_Max: 37.0,
        Steps: 4416.428571428572,
        Exercise_Hours: 0.6116666666666667,
        Sleep_Efficiency: 0.9100416021229875,
        Sleep_Duration: 7.219047619047619,
        Deep_Percentage: 0.08400447895390581,
        REM_Percentage: 0.14313816737865986,
        Bed_Time_STD: 36.538144097643574,
      },
    },
    scalingZones: {
      Exercise_Hours: {
        scalingZones: [
          0.5,
          1,
          1.5,
          2,
          2.5,
          3,
          3.5,
          4,
          4.5,
          5,
          5.5,
          6,
          6.5,
          7,
          7.5,
          8,
          8.5,
        ],
        scalingZoneWeights: [
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          8,
          9,
          10,
          11,
          12,
          13,
          14,
          15,
          16,
        ],
      },
      RHR: {
        scalingZones: [
          30,
          63.05,
          90,
        ],
        scalingZoneWeights: [
          -1,
          1,
          0,
          -1,
        ],
      },
      Sleep_Efficiency: {
        scalingZones: [
          0.85,
          0.9,
        ],
        scalingZoneWeights: [
          -1,
          0.5,
          1.5,
        ],
      },
      Sleep_Duration: {
        scalingZones: [
          6,
          7,
          8,
          10,
        ],
        scalingZoneWeights: [
          -1,
          -0.25,
          1,
          -0.25,
          -1,
        ],
      },
      Deep_Percentage: {
        scalingZones: [
          0.08,
          0.13,
          0.28,
        ],
        scalingZoneWeights: [
          -1,
          0,
          1,
          0,
        ],
      },
      Bed_Time_STD: {
        scalingZones: [
          30,
          60,
        ],
        scalingZoneWeights: [
          1.5,
          0.75,
          -1,
        ],
      },
      Steps: {
        scalingZones: [
          2000,
          4000,
          6000,
          8000,
          10000,
          12000,
        ],
        scalingZoneWeights: [
          -4,
          -2,
          -1,
          0,
          1,
          2,
          3,
        ],
      },
      REM_Percentage: {
        scalingZones: [
          0.12,
          0.2,
          0.35,
        ],
        scalingZoneWeights: [
          -1,
          0,
          1,
          0,
        ],
      },
      Vo2_Max: {
        scalingZones: [
          24,
          26.9,
          30,
        ],
        scalingZoneWeights: [
          -1,
          -0.5,
          0,
          1,
        ],
      },
    },
    HHS_status: 200,
    columns_removed: [],
    Provider_Tag: "FITBIT",
    mostRecentWeek: 13,
    Days_Per_Week_With_Steps: 6.5,
  },
  score_percentile: 47,
  habit_score_history_dict: {
    "2025-02-17": 0.9,
    "2025-02-24": 1.7,
    "2025-03-03": 1.7,
    "2025-03-10": 1.3,
    "2025-03-17": 1.7,
    "2025-03-24": 1.7,
  },
  lowest_habit: "Steps",
  absolute_score_level: "Stable",
  HHS_status: 200,
  mostRecentWeek: 13,
};

// The HHSReport component is structured as follows:
// Gauge Chart: Displays the user's current habit score percentile.
// Habit Score History Chart: Shows the trend of habit scores over time.
// Contributing Habits: Lists habits contributing positively or negatively to the user's score.
// InnerAge Affecting Habits: Highlights habits impacting the user's biological age.
// Pick Your Habit Goal: Provides actionable tips to improve the lowest-scoring habit.
// BedTime History Chart: Displays historical data for bedtime consistency with zones.
// About This Report: Explains the purpose of the report.
// Want More Habits?: Encourages users to fill out their daily journal for more insights.

const HHSReport = (props: { match: any }) => {
  const { reportToken } = props.match.params;
  const [reportData, setReportData] = useState<any>(null);

  useEffect(() => {
    (async () => {
      const reportDataResponse = await reportHubApi.getReportView(reportToken);
      setReportData(reportDataResponse);
      // setReportData(data);
    })();
  }, []);

  if (!reportData) {
    return <Spinner show mini/>;
  }

  const chat = document.getElementById('gorgias-chat-container');
  if (chat) {
    chat.style.display = 'none';
  }

  return <HHSReportView reportData={reportData} />;
};

export default HHSReport;
